import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServer } from "../Context/ServerContext";
import { useLanguage } from "../Context/LanguageContext";
import { useThemeMode } from "../Context/ThemeContext";
import DashboardView from "./DashboardView";
import { ResetModal, ThresholdModal } from "./CustomModal";
import TempCycleGraphModal from "./TempCycleGraphModal";
import {
  INSTALLATION_INFO_EVENT,
  fetchInstallationInfo,
  isEnvironmentSensorId,
  isMonitoringSensorId,
} from "../utils/installationInfo";
import { subscribeResumeConnections, resumeWebSocketIfNeeded, publishConnectionType, createLatestMessagePump } from "../utils/wsResume";
import { readLivePayload } from "../utils/normalizeLivePayload";
import { useArcAlarm } from "../utils/useArcAlarm";
import { isWifiConnection } from "../utils/wifiTempAlarm";
import { isDeviceOnline } from "../utils/deviceStatus";
import { fetchArcEventLogs, fetchTempCycleLogs } from "../utils/eventLogsApi";
import { arcAlarmKey, tempAlarmKey, useAlarmTriggeredFetch } from "../utils/useAlarmTriggeredFetch";

const DISMISSED_NOTIFICATIONS_KEY = "haechieDismissedDashboardNotifications";
const MAX_DISMISSED_NOTIFICATIONS = 300;

const persistDismissedNotificationIds = (ids) => {
  try {
    const trimmed = ids.slice(-MAX_DISMISSED_NOTIFICATIONS);
    localStorage.setItem(DISMISSED_NOTIFICATIONS_KEY, JSON.stringify(trimmed));
    return trimmed;
  } catch {
    return ids;
  }
};

const TREND_SAMPLES = 60;
const ALERT_ROWS = 10;

const averageOf = (values) => {
  const nums = values.map(Number).filter((v) => Number.isFinite(v) && v > 0);
  return nums.length ? Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(1)) : null;
};

const formatDateTime = (date) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()} ${p(date.getHours())}:${p(date.getMinutes())}:${p(date.getSeconds())}`;
};

const classifySensor = (sensor) => {
  if (Number(sensor.com_state) !== 1) return "offline";
  const level = Number(sensor.arc_level) || 0;
  if (level >= 5 || sensor.arc_state) return "emergency";
  if (level >= 3) return "warning";
  return "normal";
};


export default function DashboardModern() {
  const { t } = useLanguage();
  const { isDark } = useThemeMode();
  const { serverIp } = useServer();
  const ws = useRef(null);

  const [devices, setDevices] = useState([]);
  const [connectionType, setConnectionType] = useState("");
  const [eventLogs, setEventLogs] = useState([]);
  const [tempEventLogs, setTempEventLogs] = useState([]);
  const [tempGraphEvent, setTempGraphEvent] = useState(null);
  const [installStore, setInstallStore] = useState({ hlinks: {} });
  const [lastUpdate, setLastUpdate] = useState("");
  const [selectedSerial, setSelectedSerial] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState(() => {
    try {
      const raw = localStorage.getItem(DISMISSED_NOTIFICATIONS_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((id) => typeof id === "string") : [];
    } catch {
      return [];
    }
  });
  const notificationRef = useRef(null);
  const [tempTrend, setTempTrend] = useState([]);

  useEffect(() => {
    if (!serverIp) return;
    const load = () => fetchInstallationInfo(serverIp).then(setInstallStore);
    load();
    const onUpdate = () => load();
    window.addEventListener(INSTALLATION_INFO_EVENT, onUpdate);
    return () => window.removeEventListener(INSTALLATION_INFO_EVENT, onUpdate);
  }, [serverIp]);

  useEffect(() => {
    if (!serverIp) return;
    let reconnectTimeout;
    let isMounted = true;
    const pump = createLatestMessagePump((payload) => {
      if (!isMounted) return;
      const dataRoot = payload.data || payload;
      const devicesList = dataRoot.devices || [];
      if (!Array.isArray(devicesList) || devicesList.length === 0) return;

      let newConnType = "Offline";
      if (dataRoot.connection_state === "tcpip") newConnType = "TCP/IP";
      else if (dataRoot.connection_state === "wifi") newConnType = "WiFi";
      else if (dataRoot.connection_state === "modebus") newConnType = "TCP/IP MODBUS";
      setConnectionType(newConnType);
      publishConnectionType(newConnType);

      if (dataRoot.arc_logs_latest) {
        // Keep as soft fallback only if event log stream has not filled yet
        setEventLogs((prev) => (prev?.length ? prev : [...dataRoot.arc_logs_latest]));
      }
      // Temp + arc alert panels are fed from cloud REST log APIs (below).
      startTransition(() => setDevices(devicesList));

      const onlineDevices = devicesList.filter(isDeviceOnline);
      const allSensors = onlineDevices
        .flatMap((d) => d.sensors || [])
        .filter((s) => isMonitoringSensorId(s.sensor_id) && Number(s.com_state) === 1);
      const temps = allSensors
        .map((s) => s.ir_temperature)
        .filter((v) => Number.isFinite(v) && v > 0);
      const minT = temps.length ? Math.min(...temps) : null;
      const maxT = temps.length ? Math.max(...temps) : null;
      const avgT = temps.length ? Number(((minT + maxT) / 2).toFixed(1)) : null;
      const envSensors = onlineDevices
        .flatMap((d) => d.sensors || [])
        .filter((s) => isEnvironmentSensorId(s.sensor_id) && Number(s.com_state) === 1);
      const env = averageOf(envSensors.map((s) => s.ambient_temp));
      const hum = averageOf(envSensors.map((s) => s.humidity));
      const now = new Date();
      const stamp = now.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      setLastUpdate(formatDateTime(now));
      setTempTrend((prev) =>
        [...prev, { time: stamp, min: minT, avg: avgT, max: maxT, env, hum }].slice(-TREND_SAMPLES)
      );
    }, 150);

    const connect = () => {
      
      ws.current = new WebSocket(`ws://${serverIp}:8050/ws/sensor-data/`);
      
      ws.current.onopen = () => console.log("DashboardModern WS connected");
      ws.current.onclose = () => {
        if (!isMounted) return;
        setConnectionType("Offline");
        publishConnectionType("Offline");
        reconnectTimeout = setTimeout(connect, 1500);
      };
      ws.current.onerror = (err) => console.error("DashboardModern WS error", err);
      ws.current.onmessage = (event) => {
        try {
          pump.push(readLivePayload(event));
        } catch (err) {
          console.error("DashboardModern parse error", err);
        }
      };
    };

    const unsubscribeResume = subscribeResumeConnections((event) => {
      resumeWebSocketIfNeeded(ws.current, connect, {
        force: Boolean(event?.detail?.force),
        clearPendingRetry: () => clearTimeout(reconnectTimeout),
      });
    });

    connect();
    return () => {
      isMounted = false;
      unsubscribeResume();
      clearTimeout(reconnectTimeout);
      pump.dispose?.();
      try {
        ws.current?.close();
      } catch {
        /* ignore */
      }
    };
  }, [serverIp]);

  // Arc / temperature alert panels: fetched on open, then only when an alarm starts or clears.
  const arcKey = useMemo(() => arcAlarmKey(devices), [devices]);
  const tempKey = useMemo(() => tempAlarmKey(devices), [devices]);
  const loadArcLogs = useCallback(
    () => fetchArcEventLogs().then((logs) => setEventLogs(logs.slice(0, 20))).catch(() => {}),
    []
  );
  const loadTempLogs = useCallback(
    () => fetchTempCycleLogs().then((logs) => setTempEventLogs(logs.slice(0, 20))).catch(() => {}),
    []
  );
  useAlarmTriggeredFetch(Boolean(serverIp), arcKey, loadArcLogs);
  useAlarmTriggeredFetch(Boolean(serverIp), tempKey, loadTempLogs);

  useEffect(() => {
    if (!devices.length) {
      setSelectedSerial("");
      return;
    }
    const stillThere = devices.some((d) => d.device_info?.serial_number === selectedSerial);
    if (!stillThere) setSelectedSerial(devices[0].device_info?.serial_number || "");
  }, [devices, selectedSerial]);

  // Prefer H-Link with active arc (emergency first, then warning)
  const arcPriority = useMemo(() => {
    let bestSerial = null;
    let bestSev = 0;
    for (const d of devices) {
      const serial = d.device_info?.serial_number;
      if (!serial || !isDeviceOnline(d)) continue;
      let sev = 0;
      for (const s of (d.sensors || []).filter((x) => isMonitoringSensorId(x.sensor_id) && Number(x.com_state) === 1)) {
        if (s.arc_state || (s.arc_level || 0) >= 5) sev = Math.max(sev, 2);
        else if ((s.arc_level || 0) >= 3) sev = Math.max(sev, 1);
      }
      if (sev > bestSev) {
        bestSev = sev;
        bestSerial = serial;
      }
    }
    return { serial: bestSerial, sev: bestSev };
  }, [devices]);
  const arcPrioritySerial = arcPriority.serial;
  const arcSignature = `${arcPriority.serial || ""}:${arcPriority.sev}`;

  // Jump only when a new arc appears or escalates, so the user can still switch H-Links afterwards.
  useEffect(() => {
    if (arcPrioritySerial) setSelectedSerial(arcPrioritySerial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arcSignature]);

  /** Live IR sensors with COM connected (com_state === 1) on online H-LINKs only. */
  const allSensorsFlat = useMemo(
    () =>
      devices.filter(isDeviceOnline).flatMap((d) =>
        (d.sensors || [])
          .filter((s) => isMonitoringSensorId(s.sensor_id) && Number(s.com_state) === 1)
          .map((s) => ({
            ...s,
            _serial: d.device_info?.serial_number || "",
          }))
      ),
    [devices]
  );

  /** IR sensors on online H-LINKs that are not in COM (true no-response). */
  const noComSensorCount = useMemo(
    () =>
      devices
        .filter(isDeviceOnline)
        .reduce(
          (n, d) =>
            n +
            (d.sensors || []).filter(
              (s) => isMonitoringSensorId(s.sensor_id) && Number(s.com_state) !== 1
            ).length,
          0
        ),
    [devices]
  );

  const { audioRef, selectedSound } = useArcAlarm(serverIp, allSensorsFlat);

  const kpis = useMemo(() => {
    // Total Sensors = COM-connected only (do not inflate with offline-H-LINK stale COM).
    const total = allSensorsFlat.length;
    const counts = { normal: 0, warning: 0, emergency: 0, offline: noComSensorCount };
    allSensorsFlat.forEach((s) => {
      counts[classifySensor(s)] += 1;
    });
    const denom = total + noComSensorCount;
    const pct = (n) => (denom ? (n / denom) * 100 : 0);
    return {
      total,
      ...counts,
      pctNormal: pct(counts.normal),
      pctWarning: pct(counts.warning),
      pctEmergency: pct(counts.emergency),
      pctOffline: pct(counts.offline),
    };
  }, [allSensorsFlat, noComSensorCount]);

  const activeDevice = useMemo(
    () => devices.find((d) => d.device_info?.serial_number === selectedSerial) || devices[0] || null,
    [devices, selectedSerial]
  );

  const recentAlerts = useMemo(() => {
    const levelLabel = (lvl) => {
      if (lvl >= 5) return "Emergency";
      if (lvl >= 4) return "Critical";
      if (lvl >= 3) return "Warning";
      if (lvl >= 2) return "E-Warning";
      return "Spark";
    };
    const toHm = (raw) => {
      if (!raw) return "—";
      const text = String(raw).trim();
      const match = text.match(/(\d{1,2}):(\d{2})/);
      if (match) return `${match[1].padStart(2, "0")}:${match[2]}`;
      return text.slice(0, 5);
    };
    const messageFor = (sensorId, level) =>
      `Sensor ${sensorId} reported ${levelLabel(level)} (arc level ${level}).`;

    // Latest arc events (newest first) — no today-only filter so panels stay populated
    const fromLogs = (eventLogs || [])
      .filter((log) => String(log.event_type || "arc").toLowerCase() !== "temperature")
      .slice(0, ALERT_ROWS)
      .map((log) => {
        const level = Number(log.arc_level) || 0;
        const label = levelLabel(level);
        const timeRaw = log.time || `${log.date || ""} ${log.time || ""}`;
        return {
          id: `log-${log.sensor_id}-${log.date || ""}-${log.time || ""}-${level}`,
          sensorId: log.sensor_id,
          serial: log.serial_number || log.serial || "",
          date: log.date || "",
          level: label,
          time: toHm(timeRaw),
          tone: level >= 5 ? "red" : level >= 3 ? "amber" : "slate",
          message: messageFor(log.sensor_id, level),
        };
      });

    if (fromLogs.length) return fromLogs;

    // Live fallback only when no persisted arc logs
    return allSensorsFlat
      .filter((s) => (s.arc_level || 0) >= 3 || s.arc_state)
      .slice(0, ALERT_ROWS)
      .map((s) => {
        const level = Number(s.arc_level) || (s.arc_state ? 3 : 0);
        const label = levelLabel(level);
        return {
          id: `live-${s._serial}-${s.sensor_id}-${level}`,
          sensorId: s.sensor_id,
          serial: s._serial,
          level: label,
          time: toHm(lastUpdate.split(" ")[1] || lastUpdate),
          tone: level >= 5 ? "red" : "amber",
          message: messageFor(s.sensor_id, level),
        };
      });
  }, [eventLogs, allSensorsFlat, lastUpdate]);

  const recentTempAlerts = useMemo(() => {
    const toHm = (raw) => {
      if (!raw) return "—";
      const text = String(raw).trim();
      const match = text.match(/(\d{1,2}):(\d{2})/);
      if (match) return `${match[1].padStart(2, "0")}:${match[2]}`;
      return text.slice(0, 5);
    };

    const wifiTemp = isWifiConnection(connectionType);
    return (tempEventLogs || []).slice(0, ALERT_ROWS).map((log) => {
      const temp = Number(log.temperature ?? log.temprature);
      let thr = null;
      if (!wifiTemp) {
        const thrFromLog = Number(
          log.threshold ?? log.ir_alarm_set_temp ?? log.threshold_value
        );
        thr = Number.isFinite(thrFromLog) && thrFromLog > 0 ? thrFromLog : null;
        if (thr == null) {
          const sid = Number(log.sensor_id);
          const live = (allSensorsFlat || []).find(
            (s) => Number(s.sensor_id) === sid
          );
          const liveThr = Number(live?.ir_alarm_set_temp || live?.threshold_value);
          if (Number.isFinite(liveThr) && liveThr > 0) thr = liveThr;
        }
      }
      const tempLabel = Number.isFinite(temp) ? `${temp}°` : "—";
      const detail = wifiTemp
        ? tempLabel
        : `${tempLabel} / ${thr != null ? `${thr}°` : "—"}`;
      return {
        id: `temp-${log.id ?? log.sensor_id}-${log.date || ""}-${log.time || ""}`,
        logId: log.id,
        sensorId: log.sensor_id,
        serial: log.serial_number || log.serial || "",
        date: log.date,
        fullTime: log.time,
        temperature: Number.isFinite(temp) ? temp : null,
        threshold: thr,
        detail,
        time: toHm(log.time || ""),
        tone: "amber",
        level: "Temperature",
        kind: "temperature",
        message: `Sensor ${log.sensor_id} temperature overheat (${detail}).`,
      };
    });
  }, [tempEventLogs, allSensorsFlat, connectionType]);

  const notifications = useMemo(() => {
    const arcItems = (recentAlerts || []).map((a) => ({
      ...a,
      kind: a.kind || "arc",
    }));
    const tempItems = (recentTempAlerts || []).map((a) => ({
      ...a,
      kind: "temperature",
    }));
    return [...arcItems, ...tempItems].filter(
      (a) => !dismissedNotificationIds.includes(a.id)
    );
  }, [recentAlerts, recentTempAlerts, dismissedNotificationIds]);

  const dismissNotification = useCallback((id) => {
    setDismissedNotificationIds((prev) => {
      if (prev.includes(id)) return prev;
      return persistDismissedNotificationIds([...prev, id]);
    });
  }, []);

  const dismissAllNotifications = useCallback(() => {
    setDismissedNotificationIds((prev) => {
      const next = new Set(prev);
      recentAlerts.forEach((a) => next.add(a.id));
      recentTempAlerts.forEach((a) => next.add(a.id));
      return persistDismissedNotificationIds([...next]);
    });
  }, [recentAlerts, recentTempAlerts]);

  useEffect(() => {
    if (!showNotifications) return undefined;
    const onPointerDown = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowNotifications(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showNotifications]);

  const isOnline = connectionType && connectionType !== "Offline";
  const onSelectHlink = useCallback((serial) => {
    setSelectedSerial(serial);
  }, []);

  return (
    <div
      className="min-h-full -mx-3 md:-mx-4 -mt-1.5 -mb-3"
      style={{ background: isDark ? "#0b1326" : "#f4f7fb" }}
    >
      <DashboardView
        kpis={kpis}
        devices={devices}
        activeDevice={activeDevice}
        installStore={installStore}
        onSelectHlink={onSelectHlink}
        connectionType={connectionType}
        tempTrend={tempTrend}
        recentAlerts={recentAlerts}
        recentTempAlerts={recentTempAlerts}
        lastUpdate={lastUpdate}
        isOnline={isOnline}
        notifications={notifications}
        showNotifications={showNotifications}
        setShowNotifications={setShowNotifications}
        dismissNotification={dismissNotification}
        dismissAllNotifications={dismissAllNotifications}
        notificationRef={notificationRef}
        onTempGraph={setTempGraphEvent}
        hideThreshold={isWifiConnection(connectionType)}
        onReset={() => setShowResetModal(true)}
        onThreshold={() => setShowThresholdModal(true)}
        thresholdDisabled={connectionType === "WiFi"}
        t={t}
      />

      <ResetModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        deviceList={devices}
      />
      <ThresholdModal
        isOpen={showThresholdModal}
        onClose={() => setShowThresholdModal(false)}
        deviceList={devices}
      />
      <TempCycleGraphModal
        open={Boolean(tempGraphEvent)}
        event={tempGraphEvent}
        serverIp={serverIp}
        onClose={() => setTempGraphEvent(null)}
      />
      <audio ref={audioRef} loop preload="auto" style={{ display: "none" }}>
        {selectedSound ? <source src={selectedSound} /> : null}
      </audio>
    </div>
  );
}
